import React__default, { useContext, forwardRef, useEffect, useRef, useCallback } from 'react';
import { defineMessages, t, L10NContext, T } from '@sm/intl';
import { c as classNames, E as ErrorBoundary } from './vendor-b3423aed.js';
import { createUseStyles, useTheme } from 'react-jss';
import { sanitizeString } from '@sm/utils';
import { uniqueId, visuallyHidden } from '@wds/utils';
import ReactDOM from 'react-dom';
const useStyles$e = createUseStyles(theme => ({
  wdsIcons: ({
    color,
    size
  }) => {
    var _a, _b, _c;

    const fontSize = size ? {
      fontSize: theme.iconSize[size]
    } : {
      fontSize: 'inherit'
    };
    const chosenColor = // @ts-expect-error Fallback styles are acceptable
    ((_a = theme.palette) === null || _a === void 0 ? void 0 : _a.text[color]) || ((_c = (_b = theme.palette) === null || _b === void 0 ? void 0 : _b[color]) === null || _c === void 0 ? void 0 : _c.main) || 'currentColor';
    return { ...fontSize,
      width: '1em',
      height: '1em',
      display: 'inline-block',
      verticalAlign: 'middle',
      textAlign: 'center',
      fill: chosenColor
    };
  }
})); // https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging

const getDisplayName = Component => Component.displayName || Component.name || 'Component';

const withIcon = Component => {
  const WithIcon = ({
    color,
    title,
    size,
    ...rest
  }) => {
    const classes = useStyles$e({
      color,
      size
    });
    return React__default.createElement("svg", {
      className: classes.wdsIcons,
      viewBox: "0 0 16 16",
      preserveAspectRatio: "xMidYMid",
      "aria-label": title,
      role: title ? 'img' : 'presentation',
      ...rest
    }, React__default.createElement(Component, null));
  };

  WithIcon.displayName = `WithIcon(${getDisplayName(Component)})`;

  WithIcon.getOriginalComponent = () => Component;

  return WithIcon;
};

const useSurveyIconStyles = createUseStyles({
  svgContainer: {
    display: 'inline-block',
    width: '1em',
    height: '1em'
  }
});
let instance = 0;

function withSvgIcon(Component, displayName = 'SvgElement') {
  const SvgIcon = ({
    id,
    'aria-labelledby': ariaLabelledBy,
    title,
    viewBox = '0 0 16 16',
    className,
    ...props
  }) => {
    instance += 1;
    const styles = useSurveyIconStyles();
    const hasTitle = !!title && !ariaLabelledBy;
    const iconTitleId = id ? `${id}-icon-title` : `icon-title-${instance}`;
    return React__default.createElement("svg", {
      className: classNames(styles.svgContainer, className),
      viewBox: viewBox,
      preserveAspectRatio: "xMidYMid",
      "aria-hidden": hasTitle ? undefined : true,
      "aria-labelledby": hasTitle ? iconTitleId : ariaLabelledBy,
      role: hasTitle ? 'img' : undefined,
      focusable: "false",
      ...props,
      "data-testid": displayName
    }, hasTitle && React__default.createElement("title", {
      id: iconTitleId
    }, title), React__default.createElement(Component, null));
  };

  SvgIcon.displayName = displayName;
  return SvgIcon;
}

const DocumentXIconPath = () => React__default.createElement("path", {
  d: "m5.94.005 8.012.012C14.53.018 15 .487 15 1.067V14.95c0 .58-.47 1.05-1.05 1.05H2.05C1.47 16 1 15.53 1 14.95V4.94L5.94.004zM7 1.505V4.95c0 .541-.41.987-.936 1.044L5.95 6H2.5v8.5h11V1.514L7 1.505zM6.557 7.53 8 8.973 9.443 7.53a.765.765 0 0 1 1.083 1.082l-1.444 1.443 1.444 1.444a.765.765 0 0 1-1.083 1.082L8 11.138 6.557 12.58A.765.765 0 1 1 5.474 11.5l1.444-1.444-1.444-1.443A.765.765 0 1 1 6.557 7.53zM5.5 2.563 3.561 4.5H5.5V2.562z"
});

var IconDocumentX = withIcon(DocumentXIconPath);
const useStyles$d = createUseStyles(theme => {
  var _a, _b;

  return {
    fallbackComponent: {
      fontFamily: (_b = (_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.fontFamily) !== null && _b !== void 0 ? _b : {},
      color: theme.questionColor,
      border: `1px solid ${theme.questionColor}`,
      borderRadius: '5px',
      width: '100%',
      height: '100%',
      minHeight: '220px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& svg': {
        fill: theme.questionColor
      }
    },
    errorMessage: {
      marginLeft: `8px` // equivalent to wrench theme.spacing[2]

    }
  };
});
const COPY$5 = defineMessages({
  ERROR: {
    id: 'FallbackComponent.ERROR',
    defaultMessage: 'Error loading question',
    description: '[Type: label][Vis: high] - error message indicating a question could not be loaded'
  }
});

const FallbackComponent = () => {
  const {
    fallbackComponent,
    errorMessage
  } = useStyles$d();
  return React__default.createElement("div", {
    className: fallbackComponent
  }, React__default.createElement(IconDocumentX, null), React__default.createElement("p", {
    className: errorMessage
  }, t(COPY$5.ERROR)));
};

const withErrorBoundary = Component => {
  const displayName = Component.displayName || Component.name || 'Component';

  const ComponentWithErrorBoundary = props => React__default.createElement(ErrorBoundary, {
    FallbackComponent: FallbackComponent
  }, React__default.createElement(Component, { ...props
  }));

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  return ComponentWithErrorBoundary;
};

const useStyles$c = createUseStyles(theme => {
  var _a, _b;

  return {
    tooltip: {
      'span[data-tooltip] &': {
        display: 'none'
      },
      [`@media (min-width: ${(_b = (_a = theme.breakpoints) === null || _a === void 0 ? void 0 : _a.md.min) !== null && _b !== void 0 ? _b : '769px'})`]: {
        'span[data-tooltip]:hover &, span[data-tooltip]:focus &': {
          display: 'inline-block'
        }
      },
      position: 'absolute',
      left: ({
        center
      }) => center,
      bottom: 20,
      width: 275,
      transform: 'translate(-50%, -5px)',
      textAlign: 'center',
      zIndex: 12,
      fontSize: 13,
      '&:after': {
        position: 'absolute',
        bottom: -4,
        left: '50%',
        width: 10,
        height: 10,
        backgroundColor: 'white',
        content: '""',
        marginLeft: -5,
        transform: 'rotate(45deg)',
        boxShadow: [[3, 5, 5, 'rgb(0 0 0 / 0.07)']],
        zIndex: 13
      }
    },
    tooltipBody: {
      display: 'inline-block',
      maxWidth: 275,
      color: 'black',
      fontWeight: 300,
      fontFamily: theme.fontFamily,
      fontStyle: 'normal',
      boxShadow: [[0, 2, 10, 0, 'rgb(0 0 0 / 0.14)']],
      background: 'white',
      padding: [[12, 16]],
      lineHeight: 1.4,
      borderRadius: 2,
      textAlign: 'left'
    }
  };
});
const defaultSelectors = ['a[href]', 'button', 'textarea', 'input:not([type="hidden"])', 'select', 'details', '[tabindex]:not([tabindex="-1"])'];

const useFocusTrap = ({
  selectors = defaultSelectors
} = {}) => {
  const focusRef = React__default.useRef(null);
  const [focusableEls, setEls] = React__default.useState([]);
  React__default.useEffect(() => {
    var _a;

    setEls(Array.from((_a = focusRef === null || focusRef === void 0 ? void 0 : focusRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(selectors.join(','))));
  }, [focusRef, selectors]);

  function handleFocus(e) {
    if (focusRef.current) {
      const focusableElements = focusableEls;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else if (document.activeElement === lastElement) {
        firstElement.focus();
        e.preventDefault();
      }
    }
  }

  React__default.useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Tab') {
        handleFocus(e);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  });
  return focusRef;
};

const useStyles$b = createUseStyles(theme => {
  var _a;

  return {
    modalOverlay: {
      zIndex: 999,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end'
    },
    modal: {
      position: 'relative',
      width: '85vw',
      minHeight: '20vw',
      marginBottom: 20,
      background: 'white',
      borderRadius: 2
    },
    closeBtn: {
      position: 'absolute',
      cursor: 'pointer',
      background: 'transparent',
      right: 0,
      border: 0,
      padding: 8,
      marginTop: 0,
      color: 'black',
      zIndex: 105
    },
    tooltipText: {
      color: ((_a = theme.questionBody) === null || _a === void 0 ? void 0 : _a.color) || '#333E48',
      fontFamily: theme.fontFamily,
      fontSize: 13,
      fontWeight: 400,
      lineHeight: '1.4',
      padding: [28, 12, 16]
    }
  };
});
const COPY$4 = defineMessages({
  CLOSE_MODAL: {
    id: 'Modal.CloseModal',
    defaultMessage: 'Close tooltip',
    description: '[Type: label][Vis: Med] - close button text'
  }
});

const Modal = ({
  tooltip,
  closeModal
}) => {
  const {
    modalOverlay,
    closeBtn,
    modal,
    tooltipText
  } = useStyles$b();
  const closeRef = React__default.useRef(null);
  const tooltipId = uniqueId('tooltip-modal');
  const focusRef = useFocusTrap();

  const handleClick = e => {
    // click on button or overlay should close modal
    e.stopPropagation();

    if (e.currentTarget === e.target) {
      closeModal();
    }
  };

  React__default.useEffect(() => {
    // listen for ESC to close modal
    const handleKeydown = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [closeModal]);
  React__default.useEffect(() => {
    var _a;

    (_a = closeRef.current) === null || _a === void 0 ? void 0 : _a.focus();
  }, []);
  return React__default.createElement("div", {
    className: modalOverlay,
    onClick: handleClick,
    "aria-hidden": "true"
  }, React__default.createElement("div", {
    ref: focusRef,
    className: modal,
    role: "dialog",
    "aria-modal": "true",
    "aria-describedby": tooltipId
  }, React__default.createElement("button", {
    type: "button",
    className: closeBtn,
    ref: closeRef,
    onClick: handleClick,
    "aria-label": t(COPY$4.CLOSE_MODAL)
  }, "\u00D7"), React__default.createElement("p", {
    id: tooltipId,
    className: tooltipText
  }, tooltip)));
};
/** Displays a tooltip on hover for large screens and a modal on click for small screens */


const Tooltip = ({
  container,
  className,
  id = ''
}) => {
  var _a;
  /** container center position to display tooltip */


  const center = container.offsetWidth / 2;
  const text = (_a = container.getAttribute('data-tooltip')) !== null && _a !== void 0 ? _a : '';
  const theme = useTheme();
  const {
    tooltip,
    tooltipBody
  } = useStyles$c({
    center
  });
  const [showModal, setShowModal] = React__default.useState(false);
  const [mode, setMode] = React__default.useState('TOOLTIP');

  const closeModal = () => {
    setShowModal(false);
    container.focus();
  };

  React__default.useEffect(() => {
    container.setAttribute('role', 'tooltip'); // make tooltip anchor focusable

    if (!container.hasAttribute('tabIndex')) {
      container.setAttribute('tabIndex', '0');
    } // opens modal if matches small screen breakpoint


    const openModal = () => {
      var _a, _b;

      const mql = window.matchMedia(`(max-width: ${(_b = (_a = theme === null || theme === void 0 ? void 0 : theme.breakpoints) === null || _a === void 0 ? void 0 : _a.md.min) !== null && _b !== void 0 ? _b : '769px'})`);

      if (mql.matches) {
        setMode('MODAL');
        setShowModal(true);
      } else {
        setMode('TOOLTIP');
      }
    };

    const handleClick = () => {
      openModal();
    };

    const handleKeydown = e => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      }
    }; // listen for click, ENTER or SPACE to open modal


    container.addEventListener('click', handleClick);
    container.addEventListener('keydown', handleKeydown);
    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('keydown', handleKeydown);
    };
  }, [container, theme]);
  const modalEl = showModal ? React__default.createElement(Modal, {
    tooltip: text,
    closeModal: closeModal
  }) : null;
  const tooltipEl = React__default.createElement("div", {
    className: classNames(tooltip, className)
  }, React__default.createElement("span", {
    id: id,
    className: tooltipBody,
    "aria-hidden": "true"
  }, text));
  const child = mode === 'MODAL' ? modalEl : tooltipEl;
  const containerEl = mode === 'MODAL' ? document.body : container;
  return ReactDOM.createPortal(child, containerEl);
};

const listSpacing = {
  margin: [16, 0],
  padding: [0, 0, 0, 40]
};
const mediaReset = {
  border: 0,
  maxWidth: '100%'
};
const useStyles$a = createUseStyles({
  richTextContent: {
    // Typography
    '& a': {
      // Links are not themed by default. Users must select a link color in the editor
      color: 'blue',
      textDecoration: 'underline',
      '&:hover': {
        cursor: 'pointer'
      },
      '&:focus': {
        outline: 'currentColor auto 1px'
      }
    },
    '& ol': { ...listSpacing,
      listStyleType: 'decimal'
    },
    '& ul': { ...listSpacing,
      listStyle: 'disc outside none',
      '& ul li': {
        listStyle: 'circle'
      }
    },
    '& p': {
      margin: [13, 0]
    },
    '& b': {
      fontWeight: 'bold'
    },
    '& strong': {
      fontWeight: 'bold'
    },
    '& em': {
      fontStyle: 'italic'
    },
    '& i': {
      fontStyle: 'italic'
    },
    '& big': {
      fontSize: 'larger'
    },
    '& small': {
      fontSize: 'smaller'
    },
    '& sup': {
      fontSize: '75%',
      lineHeight: 0,
      position: 'relative',
      top: '-0.5em',
      verticalAlign: 'baseline'
    },
    // Media embeds
    '& iframe': mediaReset,
    '& img': { ...mediaReset,
      height: 'auto',
      '-ms-interpolation-mode': 'bicubic'
    },
    '& span[data-tooltip]': {
      position: 'relative',
      display: 'inline',
      borderBottom: [[1, 'dashed']],
      textDecoration: 'inherit',
      cursor: 'pointer'
    }
  }
});
/* eslint-disable @typescript-eslint/naming-convention */
// Support iframe video embeds and their matching attributes

const sanitizeOptions = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'sandbox', 'scrolling']
};

const RichText = ({
  id: elementId,
  element: Element = 'div',
  text,
  className,
  ...containerProps
}) => {
  const {
    richTextContent
  } = useStyles$a();
  const [tooltipNodes, setTooltipNodes] = React__default.useState([]);
  const tooltipRef = React__default.useRef(null);
  React__default.useEffect(() => {
    if (tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.current) {
      const el = tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.current;
      setTooltipNodes(Array.from(el.querySelectorAll('[data-tooltip]')));
    }
  }, []);
  return React__default.createElement(React__default.Fragment, null, React__default.createElement(Element, {
    id: elementId,
    className: classNames(richTextContent, className),
    dangerouslySetInnerHTML: {
      __html: sanitizeString(text, sanitizeOptions)
    },
    ref: tooltipRef,
    translate: "no",
    ...containerProps
  }), tooltipNodes.map(container => {
    const id = uniqueId('rich-text-tooltip');
    container.setAttribute('aria-describedby', id);
    return React__default.createElement(Tooltip, {
      key: id,
      container: container,
      id: id
    });
  }));
};

const textReset = {
  margin: 0
};
const useStyles$9 = createUseStyles(theme => {
  const {
    fontSize,
    ...questionTitleFormatting
  } = theme.questionTitle;
  const questionTitleFontSize = theme.isAccessible ? '22px' : fontSize !== null && fontSize !== void 0 ? fontSize : {};
  return {
    addonContainer: {
      display: 'inline-block',
      margin: [0, 5],
      verticalAlign: 'text-bottom'
    },
    container: {
      position: 'relative'
    },
    questionNumber: ({
      isRTL
    }) => ({
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    }),
    questionTitle: ({
      useDefaultFrontSize
    }) => {
      var _a;

      return { ...textReset,
        ...questionTitleFormatting,
        color: ((_a = theme.questionTitle) === null || _a === void 0 ? void 0 : _a.color) || theme.questionColor,
        fontSize: useDefaultFrontSize && !theme.isAccessible ? theme.fontSize.body : questionTitleFontSize,
        lineHeight: 1.25,
        whiteSpace: 'normal',
        wordWrap: 'break-word'
      };
    },
    requiredAsterisk: ({
      isRTL
    }) => ({
      display: 'inline-block',
      margin: isRTL ? '0 0 0 0.25em' : '0 0.25em 0 0'
    })
  };
});

const RespondentQuestionTitle = ({
  element: Element = 'span',
  addon,
  heading,
  id,
  number,
  numbered,
  required,
  useDefaultFrontSize = false,
  ...props
}) => {
  const {
    isRTL
  } = useContext(L10NContext);
  const {
    addonContainer,
    container,
    questionNumber,
    questionTitle,
    requiredAsterisk
  } = useStyles$9({
    useDefaultFrontSize,
    isRTL
  });
  return React__default.createElement(Element, {
    id: id,
    className: classNames(container, questionTitle),
    translate: "no",
    ...props
  }, required && React__default.createElement("span", {
    "aria-hidden": true,
    className: requiredAsterisk
  }, "*"), numbered && React__default.createElement("span", {
    className: questionNumber
  }, number, "."), React__default.createElement(RichText, {
    element: "span",
    text: heading
  }), addon && React__default.createElement("span", {
    className: addonContainer
  }, addon));
};

const useStyles$8 = createUseStyles(theme => {
  var _a;

  const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
  const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
  return {
    inputField: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: '18px',
      [breakpointMedium]: {
        fontSize: '16px'
      },
      fontWeight: theme.isAccessible ? theme.fontWeight.medium : theme.fontWeight.light,
      lineHeight: '1.5em',
      height: ({
        autoHeight
      }) => autoHeight ? {} : '50px',
      maxWidth: '100%',
      [breakpointXsMin]: {
        // extra 12px to account for the padding
        width: ({
          size
        }) => `calc(${size}ch + 12px)`
      },
      width: '100%',
      padding: '6px',
      border: `1px solid ${theme.answerColor}`,
      borderRadius: '0px',
      backgroundColor: '#fff',
      color: '#000',
      transition: 'all 0.1s linear',
      '&:focus, &:hover': {
        outline: `2px solid ${theme.primaryAccentColor}`
      },
      '&:read-only:not(:disabled)': {
        borderColor: 'transparent',
        backgroundColor: theme.input.bgColor,
        color: theme.isDark ? '#fff' : '#000',
        opacity: 0.5
      },
      '&:disabled': {
        opacity: 0.4
      }
    }
  };
});

function TextInput({
  className,
  autoHeight = false,
  size = 50,
  required = false,
  disabled = false,
  readOnly = false,
  onChange: handleChange,
  ...props
}, forwardedRef) {
  const {
    inputField
  } = useStyles$8({
    autoHeight,
    size
  });
  return React__default.createElement("input", {
    type: "text",
    className: classNames(inputField, className),
    disabled: disabled,
    "aria-disabled": disabled,
    required: required,
    "aria-required": required,
    readOnly: readOnly,
    "aria-readonly": readOnly,
    spellCheck: true,
    onChange: handleChange,
    ...props,
    ref: forwardedRef
  });
}

var TextInput$1 = forwardRef(TextInput);

const createFieldId = questionId => `question-field-${questionId}`;

const createLabelId = questionId => `question-title-${questionId}`;

const createErrorId = questionId => `${questionId}-validation-message`;

const useStyles$7 = createUseStyles(theme => {
  var _a;

  return {
    button: {
      color: theme.nextButton.color,
      backgroundColor: theme.nextButton.backgroundColor,
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontSize: 15,
      border: [1, 'solid', 'transparent'],
      borderRadius: 2,
      padding: [1, 6],
      maxWidth: '100%',
      minWidth: 60,
      minHeight: 40,
      cursor: 'pointer',
      overflowWrap: 'break-word',
      transition: `all .2s linear`,
      '&:focus, &:hover': {
        outlineOffset: 5,
        outline: `1px solid ${theme.answerColor}`,
        // Safari seems to have an outline bug, may not render without boxshadow
        boxShadow: '0 0 4px transparent'
      }
    }
  };
}); // This might need to be a prop later once confirmed

const MAX_LENGTH = 20;

function Button({
  text = 'OK',
  type = 'button',
  className,
  ...props
}) {
  const {
    button
  } = useStyles$7();
  const slicedText = text.length <= MAX_LENGTH ? text : text.slice(0, MAX_LENGTH);
  return (// eslint-disable-next-line react/button-has-type
    React__default.createElement("button", {
      className: classNames(button, className),
      type: type,
      ...props
    }, slicedText)
  );
}

const useStyles$6 = createUseStyles({
  visuallyHidden: visuallyHidden
});

const VisuallyHidden = ({
  children,
  element: Element
}) => {
  const {
    visuallyHidden
  } = useStyles$6();
  return React__default.createElement(Element, {
    className: visuallyHidden
  }, children);
};

const useStyles$5 = createUseStyles({
  paddingBox: ({
    padding
  }) => {
    return {
      paddingTop: (padding === null || padding === void 0 ? void 0 : padding.top) || 0,
      paddingBottom: (padding === null || padding === void 0 ? void 0 : padding.bottom) || 0,
      paddingLeft: (padding === null || padding === void 0 ? void 0 : padding.left) || 0,
      paddingRight: (padding === null || padding === void 0 ? void 0 : padding.right) || 0
    };
  }
});

const QuestionSpacing = ({
  padding,
  children
}) => {
  const {
    paddingBox
  } = useStyles$5({
    padding
  });
  return React__default.createElement("div", {
    className: paddingBox,
    "data-testid": "QuestionSpacing__paddingBox"
  }, children);
};
/**
 * Hook that calls the provided callback function when the ref element is blurred,
 * only if the next focus is outside the ref element
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useOnFocusLeave(ref, ()=> onLeaveQuestion());
 * <div rf={ref}><input /></div>
 */


function useOnFocusLeave(ref, focusCallback) {
  useEffect(() => {
    const ele = ref === null || ref === void 0 ? void 0 : ref.current;

    function handleFocus(e) {
      if (!(ele === null || ele === void 0 ? void 0 : ele.contains(e.relatedTarget))) {
        focusCallback(ref);
      }
    }

    ele === null || ele === void 0 ? void 0 : ele.addEventListener('focusout', handleFocus);
    return () => {
      ele === null || ele === void 0 ? void 0 : ele.removeEventListener('focusout', handleFocus);
    };
  }, [ref, focusCallback]);
}

const useStyles$4 = createUseStyles({
  footerRow: ({
    hasFooterPadding
  }) => ({
    paddingTop: hasFooterPadding ? 20 : 0
  }),
  containerStyles: {
    position: 'relative',
    transition: 'opacity 500ms ease-out'
  },
  errorRow: {
    marginBottom: 10
  },
  formReset: {
    border: 0,
    minWidth: 0
  },
  headerRow: {
    marginBottom: 24
  }
});
const COPY$3 = defineMessages({
  QUESTION_TITLE: {
    id: 'QuestionFieldLayoutTemplate.QUESTION_TITLE',
    defaultMessage: 'Question Title',
    description: '[Type: Label][Vis: low] - question landmark for screen readers only'
  }
});

const QuestionFieldLayoutTemplate = ({
  footer,
  hasFooterPadding,
  children,
  clickShield,
  error,
  id,
  padding,
  title,
  onSubmit,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onLeave = () => {},
  ...props
}) => {
  const {
    footerRow,
    containerStyles,
    formReset,
    errorRow,
    headerRow
  } = useStyles$4({
    hasFooterPadding,
    clickShield
  });
  const containerRef = useRef(null);
  const handleKeydown = useCallback(event => {
    if (event.key === 'Enter') {
      onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit();
    }
  }, [onSubmit]);
  const fieldsetRef = useRef(null);
  useOnFocusLeave(fieldsetRef, onLeave);
  useEffect(() => {
    const {
      current: container
    } = containerRef;
    container === null || container === void 0 ? void 0 : container.addEventListener('keydown', handleKeydown);
    return () => {
      return container === null || container === void 0 ? void 0 : container.removeEventListener('keydown', handleKeydown);
    };
  }, [containerRef, handleKeydown]);
  return React__default.createElement("div", {
    ref: containerRef,
    id: id,
    ...props,
    className: containerStyles
  }, React__default.createElement(VisuallyHidden, {
    element: "div"
  }, React__default.createElement(T, {
    desc: COPY$3.QUESTION_TITLE
  })), React__default.createElement(QuestionSpacing, {
    padding: padding
  }, error && React__default.createElement("div", {
    className: errorRow
  }, error), React__default.createElement("fieldset", {
    ref: fieldsetRef,
    className: formReset
  }, React__default.createElement("legend", {
    className: headerRow
  }, title), children), footer && React__default.createElement("div", {
    className: footerRow
  }, footer)));
};

createUseStyles(theme => ({
  containerVertical: {
    display: 'initial'
  },
  containerHorizontal: {
    display: 'block'
  },

  /** shared between horizontal and vertical columns */
  column: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  columnHorizontal: {},

  /** Horizontal Column that adjust width to content width */
  columnHorizontalAutoAdjust: {
    width: 'auto'
  },
  [`@media (min-width: ${theme.breakpoints.xs.min})`]: {
    containerVertical: {
      display: 'flex'
    },
    containerHorizontal: {
      display: 'flex',
      flexWrap: 'wrap'
    }
  },
  [`@media only screen and (min-width: ${theme.breakpoints.lg.min})`]: {
    columnHorizontal: {
      width: '19%'
    }
  },
  [`@media (min-width: ${theme.breakpoints.sm.min}) and (max-width: ${theme.breakpoints.md.max})`]: {
    columnHorizontal: {
      width: '24%'
    }
  },
  [`@media only screen and (max-width: ${theme.breakpoints.xxs.max})`]: {
    columnHorizontal: {
      width: '100%',
      display: 'block'
    },
    columnHorizontalAutoAdjust: {
      width: '100%',
      display: 'block'
    }
  },
  answerLayoutCell: {
    flex: [0, 0, 'auto'] // fix for IE11 - images leaking out of label container

  }
}));
const useStyles$3 = createUseStyles(theme => ({
  icon: {
    backgroundClip: 'padding-box',
    backgroundColor: theme.primaryAccentColor,
    borderRadius: 3,
    color: theme.secondaryAccentColor,
    fontSize: theme.fontSize.body,
    padding: [0, 5]
  },
  iconWrapper: {
    marginRight: 5
  },
  validationMessage: {
    fontFamily: theme.fontFamily,
    lineHeight: 1.5,
    color: theme.questionBody.color,
    fontWeight: theme.fontWeight.light,
    fontSize: 14
  }
}));

const ValidationMessage = ({
  children,
  id,
  tag: Wrapper = 'span',
  ...props
}) => {
  const {
    icon,
    iconWrapper,
    validationMessage
  } = useStyles$3();
  return React__default.createElement(Wrapper, {
    className: validationMessage,
    "aria-live": "polite",
    id: id,
    ...props
  }, React__default.createElement("span", {
    className: iconWrapper
  }, React__default.createElement("span", {
    "aria-hidden": "true",
    className: icon
  }, "!")), React__default.createElement("span", {
    translate: "no"
  }, children));
};

const SelectIcon = () => {
  return React__default.createElement("g", {
    className: "select-icon"
  }, React__default.createElement("polygon", {
    className: "select-arrow select-arrow-top",
    transform: "matrix(1.4619 0 0 1.4619 5.8783 5.4791)",
    points: "1.4514 -3.0633 5.5556 1.0409 -2.6529 1.0409",
    fill: "currentColor",
    stroke: "transparent",
    strokeLinejoin: "round",
    strokeWidth: ".5"
  }), React__default.createElement("polygon", {
    className: "select-arrow select-arrow-bottom",
    transform: "matrix(-1.4619 0 0 -1.4619 10.122 10.522)",
    points: "1.4514 -3.0633 5.5556 1.0409 -2.6529 1.0409",
    fill: "currentColor",
    stroke: "transparent",
    strokeLinejoin: "round",
    strokeWidth: ".5"
  }));
};

var SelectIcon$1 = withSvgIcon(SelectIcon, 'SelectIcon');
const useStyles$2 = createUseStyles(theme => {
  var _a, _b, _c, _d;

  return {
    selectStyles: {
      position: 'relative',
      display: 'inline-flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflow: 'hidden',
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : 'inherit',
      fontSize: (_b = theme.questionBody.fontSize) !== null && _b !== void 0 ? _b : '16px',
      fontWeight: (_c = theme.questionBody.fontWeight) !== null && _c !== void 0 ? _c : theme.isAccessible ? 500 : 300,
      textDecoration: (_d = theme.questionBody.textDecoration) !== null && _d !== void 0 ? _d : 'inherit',
      lineHeight: '1.5em',
      outline: 'none',
      maxWidth: '100%',
      padding: '6px 1.75em 6px 6px',
      transition: 'all 0.1s linear',
      verticalAlign: 'bottom',
      textSizeAdjust: 'auto',
      // Prevent default chevron from showing
      mozAppearance: 'none',
      webkitAppearance: 'none',
      appearance: 'none'
    },
    selectContainerStyles: {
      position: 'relative',
      display: 'inline-flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      overflow: 'hidden',
      transition: 'all 0.1s linear',
      '&:focus-within, &:hover': {
        outline: `2px solid ${theme.primaryAccentColor}`
      }
    },
    selectIconStyles: {
      position: 'absolute',
      width: '1em',
      height: '1em',
      display: 'block',
      margin: '0 .5em',
      right: 0,
      zIndex: 2,
      pointerEvents: 'none'
    },
    disabledStyles: {
      opacity: 0.4
    }
  };
});

const Select = ({
  required,
  children,
  disabled,
  ...props
}) => {
  const {
    selectStyles,
    selectContainerStyles,
    selectIconStyles,
    disabledStyles
  } = useStyles$2();
  return React__default.createElement("div", {
    className: classNames(selectContainerStyles, disabled ? disabledStyles : {})
  }, React__default.createElement(SelectIcon$1, {
    className: selectIconStyles
  }), React__default.createElement("select", {
    className: selectStyles,
    required: required,
    "aria-required": required,
    "data-testid": "select",
    disabled: disabled,
    ...props
  }, children));
};

const SelectAnswerOption = ({
  id,
  label,
  ...props
}) => {
  return React__default.createElement("option", {
    value: id,
    id: id,
    "data-testid": "select-answer-option",
    ...props
  }, label);
};

const useStyles$1 = createUseStyles(theme => {
  var _a;

  const breakpointMedium = `@media only screen and (max-width: ${theme.breakpoints.md.max})`;
  const breakpointXsMin = `@media only screen and (min-width: ${theme.breakpoints.xs.min})`;
  return {
    inputArea: {
      fontFamily: (_a = theme.questionBody.fontFamily) !== null && _a !== void 0 ? _a : {},
      fontWeight: theme.isAccessible ? theme.fontWeight.medium : theme.fontWeight.light,
      fontSize: '18px',
      [breakpointMedium]: {
        fontSize: '16px'
      },
      lineHeight: '1.15em',
      padding: '6px 60px 6px 6px',
      maxWidth: '100%',
      [breakpointXsMin]: {
        // extra 66px to account for the padding
        width: ({
          cols
        }) => `calc(${cols}ch + 66px)`
      },
      width: '100%',
      border: `1px solid ${theme.answerColor}`,
      borderRadius: '0px',
      backgroundColor: '#fff',
      color: '#000',
      transition: 'all 0.1s linear',
      verticalAlign: 'top',
      textSizeAdjust: 'auto',
      '&:focus, &:hover': {
        outline: `2px solid ${theme.primaryAccentColor}`
      },
      '&:read-only:not(:disabled)': {
        borderColor: 'transparent',
        backgroundColor: theme.input.bgColor,
        color: theme.isDark ? '#fff' : '#000',
        opacity: 0.5
      },
      '&:disabled': {
        opacity: 0.4
      }
    }
  };
});

const TextArea = ({
  className,
  disabled = false,
  required = false,
  readOnly = false,
  cols = 50,
  ...props
}) => {
  const {
    inputArea
  } = useStyles$1({
    cols
  });
  return React__default.createElement("textarea", {
    className: classNames(inputArea, className),
    disabled: disabled,
    "aria-disabled": disabled,
    required: required,
    "aria-required": required,
    readOnly: readOnly,
    "aria-readonly": readOnly,
    spellCheck: true,
    ...props
  });
};

const COPY$2 = defineMessages({
  OK_LABEL: {
    id: 'SingleTextbox.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question ok label'
  }
});

const QuestionField = ({
  active,
  disabled,
  error,
  title,
  onSubmit,
  onChange,
  onLeave,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  size = 50,
  questionId,
  questionNumber,
  required,
  survey: {
    asteriskEnabled,
    numberingEnabled
  },
  value,
  okLabel = t(COPY$2.OK_LABEL),
  variant,
  ...conditionalProps
}) => {
  const titleId = createLabelId(questionId);
  const errorId = createErrorId(questionId);
  const fieldId = createFieldId(questionId);
  const isAutoScroll = variant === 'auto-scroll';
  const showClickShield = isAutoScroll && !active; // Requires more conditional logic on integration with RAWR-109 form manager

  const showButton = isAutoScroll && active && 'showOkButton' in conditionalProps && conditionalProps.showOkButton;
  return React__default.createElement("div", null, React__default.createElement(QuestionFieldLayoutTemplate, {
    footer: showButton && React__default.createElement(Button, {
      text: okLabel,
      type: "button",
      onClick: onSubmit
    }),
    hasFooterPadding: true,
    clickShield: showClickShield,
    error: error && React__default.createElement(ValidationMessage, {
      id: errorId
    }, error),
    onLeave: onLeave,
    id: fieldId,
    padding: padding,
    title: React__default.createElement(RespondentQuestionTitle, {
      heading: title,
      id: titleId,
      number: questionNumber,
      numbered: numberingEnabled,
      required: asteriskEnabled && required
    })
  }, React__default.createElement(TextInput$1, {
    id: questionId,
    name: questionId,
    value: value,
    required: required,
    disabled: disabled,
    onChange: event => onChange === null || onChange === void 0 ? void 0 : onChange(event.target.value),
    "aria-invalid": !!error,
    "aria-describedby": error && errorId,
    "aria-labelledby": titleId,
    size: size
  })));
};

var SingleTextbox = withErrorBoundary(QuestionField);
const COPY$1 = defineMessages({
  OK_LABEL: {
    id: 'CommentBox.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question ok label'
  }
});

const CommentBox = ({
  active,
  cols = 50,
  disabled,
  error,
  title,
  onSubmit,
  onChange,
  onLeave,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  questionId,
  questionNumber,
  required,
  rows = 3,
  survey: {
    asteriskEnabled,
    numberingEnabled
  },
  value: defaultValue,
  okLabel = t(COPY$1.OK_LABEL),
  variant,
  ...conditionalProps
}) => {
  const titleId = createLabelId(questionId);
  const errorId = createErrorId(questionId);
  const fieldId = createFieldId(questionId);
  const isAutoScroll = variant === 'auto-scroll';
  const showClickShield = isAutoScroll && !active; // // Requires more conditional logic on integration with RAWR-109 form manager

  const showButton = isAutoScroll && active && 'showOkButton' in conditionalProps && conditionalProps.showOkButton;
  return React__default.createElement(QuestionFieldLayoutTemplate, {
    footer: showButton && React__default.createElement(Button, {
      text: okLabel,
      type: "button",
      onClick: onSubmit
    }),
    hasFooterPadding: true,
    clickShield: showClickShield,
    error: error && React__default.createElement(ValidationMessage, {
      id: errorId
    }, error),
    onLeave: onLeave,
    id: fieldId,
    padding: padding,
    title: React__default.createElement(RespondentQuestionTitle, {
      heading: title,
      id: titleId,
      number: questionNumber,
      numbered: numberingEnabled,
      required: asteriskEnabled && required
    })
  }, React__default.createElement(TextArea, {
    id: questionId,
    name: questionId,
    defaultValue: defaultValue,
    disabled: disabled,
    required: required,
    cols: cols,
    rows: rows,
    onChange: event => onChange === null || onChange === void 0 ? void 0 : onChange(event.target.value),
    "aria-invalid": !!error,
    "aria-describedby": error && errorId,
    "aria-labelledby": titleId
  }));
};

var CommentBox$1 = withErrorBoundary(CommentBox);
const useStyles = createUseStyles(theme => {
  var _a;

  const defaultFontSize = theme.fontSize.body;
  const {
    fontFamily = 'inherit',
    fontWeight = 'inherit',
    fontStyle = 'inherit',
    textDecoration = 'inherit',
    highlightColor = 'inherit',
    color = 'inherit'
  } = (_a = theme.questionBody) !== null && _a !== void 0 ? _a : {};
  return {
    otherStyles: {
      marginTop: '5px',
      display: 'block'
    },
    label: {
      display: 'block',
      marginTop: '10px',
      position: 'relative',
      fontFamily,
      fontWeight,
      fontStyle,
      textDecoration,
      highlightColor,
      color,
      fontSize: defaultFontSize
    }
  };
});
const COPY = defineMessages({
  OK_LABEL: {
    id: 'Dropdown.OK_LABEL',
    defaultMessage: 'OK',
    description: '[Type: button][Vis: high] - question ok label'
  }
});

const Dropdown = ({
  active,
  disabled,
  error,
  title,
  onSubmit,
  onChange,
  onLeave,
  padding = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0
  },
  questionId,
  questionNumber,
  required,
  answers,
  answerOther,
  survey: {
    asteriskEnabled,
    numberingEnabled
  },
  selectedAnswer,
  okLabel = t(COPY.OK_LABEL),
  variant,
  ...conditionalProps
}) => {
  const titleId = createLabelId(questionId);
  const errorId = createErrorId(questionId);
  const fieldId = createFieldId(questionId);
  const {
    otherStyles,
    label
  } = useStyles();
  const isAutoScroll = variant === 'auto-scroll';
  const showClickShield = isAutoScroll && !active; // Requires more conditional logic on integration with RAWR-109 form manager

  const showButton = isAutoScroll && active && 'showOkButton' in conditionalProps && conditionalProps.showOkButton && (selectedAnswer === null || selectedAnswer === void 0 ? void 0 : selectedAnswer.id) === (answerOther === null || answerOther === void 0 ? void 0 : answerOther.id);
  return React__default.createElement(QuestionFieldLayoutTemplate, {
    footer: showButton && React__default.createElement(Button, {
      text: okLabel,
      type: "button",
      onClick: onSubmit
    }),
    hasFooterPadding: true,
    clickShield: showClickShield,
    error: error && React__default.createElement(ValidationMessage, {
      id: errorId
    }, error),
    onLeave: onLeave,
    id: fieldId,
    padding: padding,
    title: React__default.createElement(RespondentQuestionTitle, {
      heading: title,
      id: titleId,
      number: questionNumber,
      numbered: numberingEnabled,
      required: asteriskEnabled && required
    })
  }, React__default.createElement(Select, {
    onChange: event => onChange === null || onChange === void 0 ? void 0 : onChange(event.target),
    disabled: disabled,
    defaultValue: selectedAnswer ? selectedAnswer.id : undefined
  }, React__default.createElement(SelectAnswerOption, null), " ", answers === null || answers === void 0 ? void 0 : answers.map(answer => {
    return React__default.createElement(SelectAnswerOption, {
      key: answer.id,
      id: answer.id,
      label: answer.label
    });
  }), (answerOther === null || answerOther === void 0 ? void 0 : answerOther.type) === 'ANSWER' && (answerOther === null || answerOther === void 0 ? void 0 : answerOther.visible) && React__default.createElement(SelectAnswerOption, {
    key: answerOther.id,
    id: answerOther.id,
    label: answerOther.label
  })), (selectedAnswer === null || selectedAnswer === void 0 ? void 0 : selectedAnswer.id) === (answerOther === null || answerOther === void 0 ? void 0 : answerOther.id) && (answerOther === null || answerOther === void 0 ? void 0 : answerOther.type) === 'ANSWER' && (answerOther === null || answerOther === void 0 ? void 0 : answerOther.visible) && React__default.createElement(TextInput$1, {
    id: questionId,
    name: questionId,
    value: selectedAnswer === null || selectedAnswer === void 0 ? void 0 : selectedAnswer.value,
    required: required,
    disabled: disabled,
    onChange: event => onChange === null || onChange === void 0 ? void 0 : onChange(event.target),
    "aria-invalid": !!error,
    "aria-describedby": error && errorId,
    "aria-labelledby": titleId,
    className: otherStyles
  }), (answerOther === null || answerOther === void 0 ? void 0 : answerOther.type) === 'COMMENT' && (answerOther === null || answerOther === void 0 ? void 0 : answerOther.visible) && React__default.createElement(React__default.Fragment, null, React__default.createElement("label", {
    id: answerOther === null || answerOther === void 0 ? void 0 : answerOther.id,
    htmlFor: answerOther === null || answerOther === void 0 ? void 0 : answerOther.id,
    className: label
  }, answerOther === null || answerOther === void 0 ? void 0 : answerOther.label), React__default.createElement(TextArea, {
    id: questionId,
    name: questionId,
    defaultValue: selectedAnswer === null || selectedAnswer === void 0 ? void 0 : selectedAnswer.value,
    disabled: disabled,
    required: required,
    cols: answerOther === null || answerOther === void 0 ? void 0 : answerOther.charsCount,
    rows: answerOther === null || answerOther === void 0 ? void 0 : answerOther.linesCount,
    onChange: event => onChange === null || onChange === void 0 ? void 0 : onChange(event.target),
    "aria-invalid": !!error,
    "aria-describedby": error && errorId,
    "aria-labelledby": titleId,
    className: otherStyles
  })));
};

var Dropdown$1 = withErrorBoundary(Dropdown);
export { CommentBox$1 as CommentBox, Dropdown$1 as Dropdown, SingleTextbox };